"use client";

import { Button } from "@pex/ui-web";
import {
  type MotionStyle,
  type MotionValue,
  motion,
  useScroll,
  useTransform,
} from "framer-motion";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { IconPaidColour } from "@/components/icons/IconPaidColour";
import { IconPersonCancelColour } from "@/components/icons/IconPersonCancelColour";
import { IconPhoneBlockColour } from "@/components/icons/IconPhoneBlockColour";

interface FeatureSectionProps {
  description: string;
  icon: ReactNode;
  title: string;
}

const featureSections: FeatureSectionProps[] = [
  {
    icon: <IconPersonCancelColour height={48} width={48} />,
    title: "90% Never Get Help",
    description:
      "Despite the growing crisis of addiction worldwide, 90% of those in need of addiction treatment never receive it. This alarming treatment gap leaves millions of individuals and families devastated by the destructive impact of substance abuse, pornography addiction, and other addictive disorders.",
  },
  {
    icon: <IconPhoneBlockColour height={48} width={48} />,
    title: "Pornography Epidemic",
    description:
      "60% of adults and 39% of young people (12–27 years) are regular consumers of pornography. The crisis extends to spiritual leaders too, with 75% of pastors ministering to congregants struggling with pornography addiction and 67% reporting personal struggles with pornography themselves.",
  },
  {
    icon: <IconPaidColour height={48} width={48} />,
    title: "R1.1 Trillion Gambled",
    description:
      "Between April 2023 and March 2024, South Africans spent R1.1 trillion on gambling. This represents a 40.2% increase from the previous year, highlighting a growing crisis.",
  },
];

export function StatsSection() {
  const [isMobile, setIsMobile] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 767);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: isMobile ? ["20% start", "end end"] : ["start start", "end end"],
  });

  return (
    <section className="relative bg-background" ref={containerRef}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="relative h-[300svh] lg:h-[300vh]">
          <div className="static grid h-full grid-cols-1 content-start items-center gap-x-20 gap-y-16 py-16 md:sticky md:top-0 md:h-[100svh] md:grid-cols-2 md:content-normal md:py-0 lg:h-screen">
            {/* Teal/blue gradient background */}
            <div
              className="pointer-events-none absolute z-0 hidden select-none overflow-hidden md:block"
              style={{
                top: 0,
                bottom: 0,
                left: "50%",
                width: "100vw",
                transform: "translateX(-50%)",
              }}
            >
              <div
                className="relative h-full w-full"
                style={{
                  maskImage:
                    "linear-gradient(to bottom, transparent 0%, black 30%, black 70%, transparent 100%)",
                  WebkitMaskImage:
                    "linear-gradient(to bottom, transparent 0%, black 30%, black 70%, transparent 100%)",
                }}
              >
                <div
                  className="absolute -bottom-[20%] -left-[10%] h-[60%] w-[80%] rounded-[100%] opacity-80"
                  style={{ background: "#D0EEF1", filter: "blur(100px)" }}
                />
                <div
                  className="absolute -right-[10%] bottom-[0%] h-[80%] w-[60%] rounded-[100%] opacity-60"
                  style={{ background: "#C8DAEF", filter: "blur(90px)" }}
                />
                <div
                  className="absolute bottom-[20%] left-[30%] h-[40%] w-[40%] rounded-[100%] opacity-40 mix-blend-screen"
                  style={{ background: "#F0FAFB", filter: "blur(80px)" }}
                />
                <div
                  className="absolute inset-0 opacity-25 mix-blend-overlay"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.5' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
                    maskImage:
                      "radial-gradient(circle at 50% 100%, black, transparent 80%)",
                    WebkitMaskImage:
                      "radial-gradient(circle at 50% 100%, black, transparent 80%)",
                  }}
                />
              </div>
            </div>

            <div className="relative z-10">
              <h2 className="heading-2 rb-5 mb-5 font-bold md:mb-6">
                The Need is Real
              </h2>
              <p className="body-lg text-muted-foreground">
                Millions are struggling with addiction, yet the vast majority
                never receive the help they desperately need. Project Exodus
                exists to bridge this gap by creating accessible pathways to
                recovery and building strong support networks in communities
                worldwide.
              </p>
              <div className="mt-6 flex w-full flex-col gap-4 sm:w-auto sm:flex-row sm:gap-6 md:mt-8">
                <Button
                  asChild
                  className="w-full sm:w-auto"
                  size="lg"
                  variant="fancy"
                >
                  <Link href="/find-help">Find Help</Link>
                </Button>
                <Button
                  asChild
                  className="w-full self-stretch sm:w-auto sm:self-center"
                  variant="link-icon"
                >
                  <Link href="/partner/overview">
                    Partner with Us
                    <ArrowRight />
                  </Link>
                </Button>
              </div>
            </div>
            <div className="relative sticky top-[25%] z-10 flex min-h-[24.5rem] flex-col items-center justify-center md:relative md:top-0 md:min-h-[auto]">
              {featureSections.map((section, index) => (
                <FeatureCard
                  index={index}
                  key={section.title}
                  scrollYProgress={scrollYProgress}
                  section={section}
                  totalSections={featureSections.length}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="absolute inset-0 -z-10 mt-[100vh]" />
    </section>
  );
}

function FeatureCard({
  section,
  index,
  totalSections,
  scrollYProgress,
}: {
  section: FeatureSectionProps;
  index: number;
  totalSections: number;
  scrollYProgress: MotionValue<number>;
}) {
  const sectionScrollStart = index / totalSections;
  const sectionScrollEnd = (index + 1) / totalSections;

  const rotate = useTransform(
    scrollYProgress,
    [sectionScrollStart, sectionScrollEnd],
    [0 + index * 3, -30]
  );
  const translateY = useTransform(
    scrollYProgress,
    [sectionScrollStart, sectionScrollEnd],
    ["0vh", "-100vh"]
  );
  const translateX = useTransform(
    scrollYProgress,
    [sectionScrollStart, sectionScrollEnd],
    ["0vw", "-10vw"]
  );

  return (
    <motion.div
      className="absolute mx-6 flex flex-col gap-6 rounded-xl border bg-white p-6 sm:gap-8 sm:p-8 md:ml-0"
      style={
        {
          rotate: index === totalSections - 1 ? "6deg" : rotate,
          translateY: index === totalSections - 1 ? undefined : translateY,
          translateX: index === totalSections - 1 ? undefined : translateX,
          zIndex: totalSections - index,
        } as MotionStyle
      }
    >
      <div className="flex h-12 w-12 items-center justify-center sm:h-16 sm:w-16">
        {section.icon}
      </div>
      <div className="flex flex-col gap-3 sm:gap-4">
        <h3 className="heading-5 font-bold">{section.title}</h3>
        <p className="body-base text-muted-foreground">{section.description}</p>
      </div>
    </motion.div>
  );
}
